/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createModal } from 'react-modal-promise';
import { trim, get, cloneDeep, set, isEmpty, uniq } from 'lodash';
import { bindActionCreators } from 'redux';
import { Loader } from 'semantic-ui-react';
import {
  ControlLabel,
  Form,
  FormControl,
  FormGroup,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import {
  InsuranceType,
  InsuranceTypeLabelMap,
} from '@upstart/harmony/lib/legos/financingTypes';
import { STATES } from '@upstart/harmony/lib/legos/constants';
import {
  Condition,
  FinanceType,
  Origin,
  ProtectionPlansSelectionType,
} from '@upstart/harmony/lib/constants';
import {
  PriceAdjustmentType,
  ProductCategory,
} from '@upstart/harmony/lib/legos/penTypes.js';
import { PenProtectionPlanSourceTypes } from './ProtectionPlansConstants';
import '../ReactModal/reactModal.scss';
import {
  requiredValidationState,
  requiredValidationStateObject,
} from '../../util/utils';
import penActions from '../../redux/pen/actions';
import { updateDealerRequest } from '../../redux/dealers/actions';
import LoadingOverlay from '../LoadingOverlay';
import {
  DEFAULT_TERM_LENGTHS,
  inStoreSelectionOptions,
  onlineSelectionOptions,
  providerRequiresRateBookCode,
} from './conf';
import { SelectRateBookCode } from './SelectRateBookCode';
import MarkupDiscount from './MarkupDiscount';
import { Input, Radio } from '../GlobalUI';
import CoverageConfiguration, {
  isCoverageOptionsValid,
} from './CoverageConfiguration/CoverageConfiguration';
import {
  getCoverageNamesAndDeductiblesOptions,
  getDefaultCoverageNamesAndDeductibles,
} from '../../util/protectionPlans';

const ProductCategoryLabelMap = {
  [ProductCategory.ACCESSORY]: 'Accessory',
  [ProductCategory.FRONT_END_ADD_ON]: 'Front End Add On',
  [ProductCategory.GAP]: 'Gap',
  [ProductCategory.INSURANCE]: 'Insurance',
  [ProductCategory.SERVICE_CONTRACT]: 'Service Contract',
  [ProductCategory.THEFT_DETERRENT_DEVICE]: 'Theft Deterrent Device',
  [ProductCategory.SURFACE_PROTECTION_PRODUCT]: 'Surface Protection Product',
};

const HELP_TOOLTIPS = {
  VIN: 'Add VIN or leave empty to get random VIN',
  MILEAGE: 'Add custom mileage or use default mileage',
};

const DEFAULT_PURCHASE_TERM = 48;

function ProductModal(props) {
  const listToString = (list) => {
    if (!list) {
      return '';
    }
    list = [].concat(list);
    return list.join('\n');
  };

  const stringToList = (string) => {
    const list = string.split('\n').map((item) => item);
    return list.filter((item) => trim(item) !== '');
  };

  const { actions, dealer, close, product, pen } = props;

  const [selectedOnline, setSelectedOnline] = useState(
    product.selectedOnline || ProtectionPlansSelectionType.None,
  );
  const [selectedInStore, setSelectedInStore] = useState(
    product.selectedInStore || ProtectionPlansSelectionType.None,
  );

  const [providers, setProviders] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [provider, setProvider] = useState(null);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const getProviderConfig = (value) =>
    providers.filter((pro) => pro.id === parseInt(value, 10))[0];

  const [providerDealerCode, setProviderDealerCode] = useState(
    product.providerDealerCode || '',
  );
  const [productDealerCode, setProductDealerCode] = useState(
    product.productDealerCode,
  );
  const [displayName, setDisplayName] = useState(product.displayName);
  const [displayHighlightedTextInput, setDisplayHighlightedTextInput] =
    useState(!!product?.highlightedText);

  const [highlightedText, setHighlightedText] = useState(
    product?.highlightedText || '',
  );
  const [description, setDescription] = useState(
    listToString(product.description),
  );

  // Old properties for markup/discount
  const [markup, setMarkup] = useState(product.markup);
  const [discount, setDiscount] = useState(product.discount);

  // New properties for markup/discount (based on deal origin)
  const [markups, setMarkups] = useState(product.markups || {});
  const [discounts, setDiscounts] = useState(product.discounts || {});

  const [priceAdjustmentTypeValue, setPriceAdjustmentTypeValue] = useState(
    product.priceAdjustmentType || PriceAdjustmentType.PERCENT,
  );

  const [image, setImage] = useState(product.image);
  const [enabled, setEnabled] = useState(product.enabled);
  const [taxed, setTaxed] = useState(product.taxed);
  const [backend, setBackend] = useState(product.backend);
  const [videoUrl, setVideoUrl] = useState(product.videoUrl);
  const [brochureUrl, setBrochureUrl] = useState(product.brochureUrl);
  const [defaultTermLength, setDefaultTermLength] = useState(() =>
    DEFAULT_TERM_LENGTHS.find((term) => term.value === product.defaultTerm),
  );

  const [defaultTermsByCondition, setDefaultTermsByCondition] = useState(() => {
    const termsByCondition = {};
    const defaultTerms = product?.defaultTermsByCondition || {};
    for (const condition in defaultTerms) {
      if (defaultTerms[condition]) {
        const defaultTermForCondition = defaultTerms[condition];
        termsByCondition[condition] = {
          value: defaultTermForCondition,
          label: `${defaultTermForCondition} months`,
        };
      }
    }
    return termsByCondition;
  });

  const [productCategorySelect, setProductCategorySelect] = useState({
    label: product.productCategory,
    value: product.productCategory,
  });
  const [submitted, setSubmitted] = useState(false);

  const [inCapCostForLease, setInCapCostForLease] = useState(
    product.inCapCostForLease ?? true,
  );
  const [inCapCostForOnePayLease, setInCapCostForOnePayLease] = useState(
    product.inCapCostForOnePayLease ?? true,
  );

  const [availableNewCash, setAvailableNewCash] = useState(
    product.availability[Condition.NEW][FinanceType.CASH],
  );
  const [availableNewLease, setAvailableNewLease] = useState(
    product.availability[Condition.NEW][FinanceType.LEASE],
  );
  const [availableNewPurchase, setAvailableNewPurchase] = useState(
    product.availability[Condition.NEW][FinanceType.PURCHASE],
  );
  const [availableUsedCash, setAvailableUsedCash] = useState(
    product.availability[Condition.USED][FinanceType.CASH],
  );
  const [availableUsedPurchase, setAvailableUsedPurchase] = useState(
    product.availability[Condition.USED][FinanceType.PURCHASE],
  );
  const [availableCpoCash, setAvailableCpoCash] = useState(
    product.availability[Condition.CPO]?.[FinanceType.CASH],
  );
  const [availableCpoPurchase, setAvailableCpoPurchase] = useState(
    product.availability[Condition.CPO]?.[FinanceType.PURCHASE],
  );
  const [retailPrice, setRetailPrice] = useState(product.retailPrice);
  const [dealerProductCodeRequired, setDealerProductCodeRequired] =
    useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [penError, setPenError] = useState(null);
  const [rateBookCode, setRateBookCode] = useState(
    product?.rateBookCode || null,
  );

  const [newVin, setNewVin] = useState(
    product.conditionInputValuesForCoverage?.[Condition.NEW]?.vin || '',
  );
  const [usedVin, setUsedVin] = useState(
    product.conditionInputValuesForCoverage?.[Condition.USED]?.vin || '',
  );
  const [usedMileage, setUsedMileage] = useState(
    product.conditionInputValuesForCoverage?.[Condition.USED]?.mileage || 50000,
  );
  const [cpoVin, setCpoVin] = useState(
    product.conditionInputValuesForCoverage?.[Condition.CPO]?.vin || '',
  );
  const [cpoMileage, setCpoMileage] = useState(
    product.conditionInputValuesForCoverage?.[Condition.CPO]?.mileage || 10000,
  );

  const [purchaseTerm, setPurchaseTerm] = useState(
    product.purchaseTerm || DEFAULT_PURCHASE_TERM,
  );

  const [randomVinObj] = useState({});

  const [insuranceType, setInsuranceType] = useState(product?.insuranceType);
  const [insuranceAddressLineOne, setInsuranceAddressLineOne] = useState(
    product?.insuranceAddress?.lineOne,
  );
  const [insuranceAddressLineTwo, setInsuranceAddressLineTwo] = useState(
    product?.insuranceAddress?.lineTwo,
  );
  const [insuranceAddressCity, setInsuranceAddressCity] = useState(
    product?.insuranceAddress?.city,
  );
  const [insuranceAddressStateCode, setInsuranceAddressStateCode] = useState(
    product?.insuranceAddress?.stateCode,
  );
  const [insuranceAddressZip, setInsuranceAddressZip] = useState(
    product?.insuranceAddress?.zip,
  );
  const [insuranceCompanyName, setInsuranceCompanyName] = useState(
    product?.insuranceCompanyName,
  );

  const form = document.forms[0];

  // These Providers require an extra ProductDealerCode
  const providerRequiresProductCode = {
    12: true, // Protective - Prod & Testing
    38: true, // US Warranty - Prod
    39: true, // US Warranty - Testing
  };

  const rateBookLookupDisabled =
    !selectedProviderId ||
    !selectedProduct ||
    !providerDealerCode ||
    (providerRequiresProductCode[selectedProviderId] && !productDealerCode);

  // STORE AVAILABLE COVERAGES AND DEDUCTIBLES HERE
  const [
    availableCoveragesAndDeductibles,
    setAvailableCoveragesAndDeductibles,
  ] = useState({});

  // STORE SELECTED COVERAGE NAME AND DEDUCTIBLES HERE
  const [coverageNamesAndDeductibles, setCoverageNamesAndDeductibles] =
    useState(product.coverageNamesAndDeductibles);

  const coverageNamesAndDeductiblesOptionsRef = useRef(
    product.coverageNamesAndDeductiblesOptions
      ? cloneDeep(product.coverageNamesAndDeductiblesOptions)
      : null,
  );

  const shouldDisplayInsuranceFields =
    productCategorySelect?.value === ProductCategory.INSURANCE;

  const selectCoverageName = (value, condition, financeType) => {
    const newCoverageNamesAndDeductibles = cloneDeep(
      coverageNamesAndDeductibles,
    );
    const coverages = availableCoveragesAndDeductibles?.[condition]?.[
      financeType
    ]
      ? availableCoveragesAndDeductibles[condition][financeType]
      : [];
    const coverage = coverages.filter((cov) => cov.coverageName === value)[0];
    set(
      newCoverageNamesAndDeductibles,
      `${condition}.${financeType}.coverageName`,
      cloneDeep(coverage.coverageName),
    );
    set(
      newCoverageNamesAndDeductibles,
      `${condition}.${financeType}.allDeductiblesForCoverage`,
      cloneDeep(coverage.allDeductiblesForCoverage),
    );
    set(
      newCoverageNamesAndDeductibles,
      `${condition}.${financeType}.deductible`,
      undefined,
    );

    setCoverageNamesAndDeductibles(newCoverageNamesAndDeductibles);
  };

  const selectProvider = (value) => {
    const selectedProvider = getProviderConfig(value);
    if (selectedProvider && selectedProvider.name) {
      setProvider(selectedProvider.name);
      setSelectedProviderId(selectedProvider.id);
      setDealerProductCodeRequired(
        providerRequiresProductCode[selectedProvider.id],
      );
      actions.getProviderProductsRequest({ provider: selectedProvider });
    }
  };

  const selectProduct = (value) => {
    const selectedProductObject = pen.providerProducts.filter(
      (p) => p.raw.productId === parseInt(value, 10),
    )[0];
    if (selectedProductObject) {
      setSelectedProduct(selectedProductObject);
    }
  };

  const getConditionFinanceTypes = () => ({
    [Condition.NEW]: {
      [FinanceType.CASH]: availableNewCash,
      [FinanceType.LEASE]: availableNewLease,
      [FinanceType.PURCHASE]: availableNewPurchase,
      vin: newVin,
    },
    [Condition.USED]: {
      [FinanceType.CASH]: availableUsedCash,
      [FinanceType.PURCHASE]: availableUsedPurchase,
      vin: usedVin,
      mileage: usedMileage ? usedMileage.toString().replace(/\D/gi, '') : '',
    },
    [Condition.CPO]: dealer.featureConfig.useCPO.enabled
      ? {
          [FinanceType.CASH]: availableCpoCash,
          [FinanceType.PURCHASE]: availableCpoPurchase,
          vin: cpoVin,
          mileage: cpoMileage ? cpoMileage.toString().replace(/\D/gi, '') : '',
        }
      : null,
  });
  const getConditionInputValuesForCoverage = () => ({
    [Condition.NEW]: {
      vin: newVin || randomVinObj[Condition.NEW] || '',
    },
    [Condition.USED]: {
      vin: usedVin || randomVinObj[Condition.USED] || '',
      mileage: usedMileage ? usedMileage.toString().replace(/\D/gi, '') : '',
    },
    [Condition.CPO]: dealer.featureConfig.useCPO.enabled
      ? {
          vin: cpoVin || randomVinObj[Condition.CPO] || '',
          mileage: cpoMileage ? cpoMileage.toString().replace(/\D/gi, '') : '',
        }
      : null,
  });

  const registerProductAndGetCoverages = (e) => {
    if (e) {
      e.preventDefault();
    }

    const conditionFinanceTypes = getConditionFinanceTypes();
    const conditionInputValuesForCoverage =
      getConditionInputValuesForCoverage();

    const pID =
      get(selectedProduct, 'raw.productId') || get(product, 'raw.productId');
    const providerId =
      get(selectedProduct, 'raw.providerId') || get(product, 'raw.providerId');
    const pdc = product.productDealerCode || productDealerCode;
    const payload = {
      productId: pID,
      dealerId: dealer.id,
      providerDealerCode,
      conditionFinanceTypes,
      providerId,
      rateBookCode,
      productDealerCode: pdc,
      conditionInputValuesForCoverage,
      purchaseTerm,
    };

    setShowSpinner(true);
    actions.getCoveragesAndDeductiblesRequest(payload);
  };

  const getProductCategories = () => {
    const categories = Object.keys(ProductCategory).map((categoryEnum) => ({
      value: ProductCategory[categoryEnum],
      label:
        ProductCategoryLabelMap[ProductCategory[categoryEnum]] ||
        ProductCategory[categoryEnum],
    }));
    return [{ value: null, label: ' ‎' }, ...categories]; // Label has special whitespace character to have a blank like as first option
  };

  const saveIsDisabled = () => {
    let disableSave = false;
    if (product.source === PenProtectionPlanSourceTypes.PROVIDER) {
      if (!provider || !selectedProduct || !providerDealerCode) {
        disableSave = true;
      }
      if (dealerProductCodeRequired && !productDealerCode) {
        return true;
      }
      if (
        !product.coverageNamesAndDeductiblesOptions &&
        Object.keys(availableCoveragesAndDeductibles).length === 0
      ) {
        return true;
      }
    }
    return disableSave;
  };

  // FIRST LOAD
  useEffect(() => {
    // IF THIS IS A PROVIDER PRODUCT
    // SET THE FOLLOWING DATA
    if (product.source === PenProtectionPlanSourceTypes.PROVIDER) {
      // Get Providers
      actions.getProvidersRequest();

      if (
        product.raw &&
        product.raw.providerId &&
        providerRequiresProductCode[product.raw.providerId] &&
        product.source === PenProtectionPlanSourceTypes.PROVIDER
      ) {
        setDealerProductCodeRequired(true);
      }

      if (
        product.raw.productId &&
        dealer.id &&
        !product.coverageNamesAndDeductiblesOptions
      ) {
        setShowSpinner(true);
        registerProductAndGetCoverages();
      }
    }
  }, []);

  useEffect(() => {
    if (product.coverageName) {
      selectCoverageName(product.coverageName);
    }

    if (pen.allProviders) {
      setProviders(pen.allProviders);
      if (product.raw.providerId && !provider) {
        selectProvider(product.raw.providerId);
      }
    }

    if (pen.providerProducts && pen.providerProducts.length > 0) {
      if (product.raw.productId && !selectedProduct) {
        selectProduct(product.raw.productId);
      }
    }

    if (pen.coverageNamesAndDeductibles && provider && selectedProduct) {
      setAvailableCoveragesAndDeductibles(pen.coverageNamesAndDeductibles);
      if (!product.raw.productId) {
        coverageNamesAndDeductiblesOptionsRef.current = null;
      }
    }

    if (!pen.fetching) {
      setShowSpinner(false);
    }

    setPenError(get(pen, 'error.error'));
  }, [pen]);

  useEffect(() => {
    if (!shouldDisplayInsuranceFields) {
      setInsuranceType(undefined);
      setInsuranceAddressLineOne(undefined);
      setInsuranceAddressLineTwo(undefined);
      setInsuranceAddressCity(undefined);
      setInsuranceAddressStateCode(undefined);
      setInsuranceAddressZip(undefined);
      setInsuranceCompanyName(undefined);
    }
  }, [shouldDisplayInsuranceFields]);

  const parseImage = (e) => {
    if (e) {
      const file = document.querySelector('#productImageUploadInput').files[0];
      setImage(file);
    }
  };

  const isInvalidVin = (vin) => vin && vin.length > 0 && vin.length !== 17;

  const getCoverageNamesIsDisabled = () => {
    if (!providerDealerCode) {
      return true;
    }
    if (dealerProductCodeRequired && !productDealerCode) {
      return true;
    }
    if (providerRequiresRateBookCode[selectedProviderId] && !rateBookCode) {
      return true;
    }

    if (isInvalidVin(newVin) || isInvalidVin(usedVin) || isInvalidVin(cpoVin)) {
      return true;
    }
    return false;
  };

  const getDefaultTermByConditionPayload = (defaultTerms = {}) => {
    const payload = {};
    for (const condition in defaultTerms) {
      if (defaultTerms[condition]) {
        payload[condition] = defaultTerms[condition].value;
      }
    }
    return payload;
  };

  const saveProduct = async () => {
    setShowSpinner(true);
    const dealerId = dealer.id;
    const productId = product.id;
    const availability = getConditionFinanceTypes();
    const conditionInputValuesForCoverage =
      getConditionInputValuesForCoverage();
    const formData = new FormData();
    const newProductData = {
      displayName,
      description: stringToList(description),
      markup,
      discount,
      markups,
      discounts,
      enabled,
      priceAdjustmentType: priceAdjustmentTypeValue,
      taxed,
      backend,
      retailPrice: parseFloat(retailPrice),
      availability,
      videoUrl,
      brochureUrl,
      providerDealerCode,
      productDealerCode,
      selectedOnline,
      selectedInStore,
      coverageNamesAndDeductibles: getDefaultCoverageNamesAndDeductibles(
        coverageNamesAndDeductiblesOptionsRef.current,
      ),
      defaultTerm: defaultTermLength?.value,
      defaultTermsByCondition: getDefaultTermByConditionPayload(
        defaultTermsByCondition,
      ),
      rateBookCode,
      productCategory: productCategorySelect.value,
      conditionInputValuesForCoverage,
      insuranceType,
      insuranceAddress: {
        lineOne: insuranceAddressLineOne,
        lineTwo: insuranceAddressLineTwo,
        city: insuranceAddressCity,
        stateCode: insuranceAddressStateCode,
        zip: insuranceAddressZip,
      },
      insuranceCompanyName,
      highlightedText,
      purchaseTerm,
      inCapCostForLease,
      inCapCostForOnePayLease,
    };
    if (coverageNamesAndDeductiblesOptionsRef.current) {
      newProductData.coverageNamesAndDeductiblesOptions = {
        ...coverageNamesAndDeductiblesOptionsRef.current,
      };
      coverageNamesAndDeductiblesOptionsRef.current = null;
    }

    // IF THIS IS A PROVIDER PRODUCT
    // APPEND THE RAW PRODUCT DATA FROM PEN
    if (
      product.source === PenProtectionPlanSourceTypes.PROVIDER &&
      !product.raw.productId &&
      selectedProduct.raw
    ) {
      newProductData.raw = cloneDeep(selectedProduct.raw);
    }

    formData.append(
      'product',
      JSON.stringify({ ...product, ...newProductData }),
    );
    if (image) {
      formData.append('image', image);
    }
    if (productId) {
      actions.editProductRequest({ formData, dealerId, productId });
    } else {
      await actions.createProductRequest({ formData, dealerId });
      if (isEmpty(get(dealer, 'penConfig.enableInUI')) && pen?.penConfig) {
        const dealerUpdatePayload = {
          id: dealer.id,
          penConfig: {
            enableInUI: cloneDeep(pen.penConfig.enableInUI),
            multiBundlesInDeal: cloneDeep(pen.penConfig.multiBundlesInDeal),
            hideProductPrices: cloneDeep(pen.penConfig.hideProductPrices),
            showTotalCost: cloneDeep(pen.penConfig.showTotalCost),
            showDailyPrice: cloneDeep(pen.penConfig.showDailyPrice),
            useSelectedOfferTermsAndMiles: cloneDeep(
              pen.penConfig.useSelectedOfferTermsAndMiles,
            ),
          },
        };

        const pps = get(pen, 'penConfig.protectionPlanSource');
        if (pps) {
          dealerUpdatePayload.protectionPlanSource = pps;
        }
        actions.updateDealer({
          dealer: dealerUpdatePayload,
          isDiffObject: true,
        });
      }
    }

    close(true);
  };

  const isCoverageNamesAndDeductiblesOptionsValid = () => {
    if (!coverageNamesAndDeductiblesOptionsRef?.current) {
      return true;
    }
    // eslint-disable-next-line guard-for-in
    for (const condition in coverageNamesAndDeductiblesOptionsRef?.current) {
      for (const financeType in coverageNamesAndDeductiblesOptionsRef?.current[
        condition
      ]) {
        if (
          !isCoverageOptionsValid(
            coverageNamesAndDeductiblesOptionsRef?.current[condition][
              financeType
            ],
          )
        ) {
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = (e) => {
    setSubmitted(true);

    if (
      form.checkValidity() === false ||
      !isCoverageNamesAndDeductiblesOptionsValid()
    ) {
      e.preventDefault();
    } else {
      saveProduct();
    }
  };

  const handleClose = () => {
    // clear coverages from state on close
    pen.coverageNamesAndDeductibles[product.id] = [];
    coverageNamesAndDeductiblesOptionsRef.current = null;
    actions.resetCoveragesAndDeductibles();
    close(false);
  };

  // Loop though available Coverages and Deductibles
  // Render a pair of inputs to select coverageName and Deductible
  const renderConditionsAndFinanceTypeTabs = () => {
    if (
      (!product.coverageNamesAndDeductiblesOptions &&
        Object.keys(availableCoveragesAndDeductibles).length === 0) ||
      pen.fetchingCov
    ) {
      return null;
    }
    const availableCoverages = getCoverageNamesAndDeductiblesOptions(
      availableCoveragesAndDeductibles, // for determining the tab options and form default data
      coverageNamesAndDeductibles, // for determining the default selected coverage option
      coverageNamesAndDeductiblesOptionsRef.current, // for retaining the current form inputs
    );

    if (Object.keys(availableCoverages).length === 0) {
      return null;
    }

    coverageNamesAndDeductiblesOptionsRef.current = availableCoverages;

    return (
      <CoverageConfiguration
        availableCoveragesAndDeductibles={
          coverageNamesAndDeductiblesOptionsRef.current
        }
        submitted={submitted}
        coverageNamesAndDeductiblesRef={coverageNamesAndDeductiblesOptionsRef}
      />
    );
  };

  const canShowSelectRateBookCode = () =>
    get(selectedProduct, 'raw.productId') &&
    providerRequiresRateBookCode[selectedProviderId];

  const canShowRemainingForm = () => {
    if (
      !product.coverageNamesAndDeductiblesOptions &&
      product.source === PenProtectionPlanSourceTypes.PROVIDER &&
      Object.keys(availableCoveragesAndDeductibles).length === 0 &&
      (!pen.fetching || !pen.fetchingCov)
    ) {
      return false;
    }
    return true;
  };

  // Ally products cannot be marked up or discounted
  const isProviderAlly = useMemo(() => {
    if (provider === 'Ally (APP)') {
      setMarkup(0);
      setDiscount(0);
      return true;
    }
    return false;
  }, [provider]);

  const penCoverageNamesAndDeductibles = pen?.coverageNamesAndDeductibles || {};
  const availableTerms = useMemo(() => {
    const result = {};
    if (penCoverageNamesAndDeductibles) {
      for (const condition in penCoverageNamesAndDeductibles) {
        if (penCoverageNamesAndDeductibles[condition]) {
          const coveragesForCondition =
            penCoverageNamesAndDeductibles[condition];
          const termsForCondition = [];
          for (const financeType in coveragesForCondition) {
            if (coveragesForCondition[financeType]) {
              const converagesForFinanceType =
                coveragesForCondition[financeType];
              if (
                converagesForFinanceType &&
                converagesForFinanceType.length > 0
              ) {
                converagesForFinanceType.forEach((c) => {
                  termsForCondition.push(...c.allTermsForCoverage);
                });
              }
            }
          }
          result[condition] = uniq(termsForCondition);
        }
      }
    }
    return result;
  }, [penCoverageNamesAndDeductibles]);

  const onDefaultTermForConditionChange = (term, condition) => {
    const updatedDefaultTermsByCondition = {
      ...defaultTermsByCondition,
      [condition]: term,
    };
    setDefaultTermsByCondition(updatedDefaultTermsByCondition);
  };

  const renderDefaultTermsByCondition = () => {
    if (isEmpty(availableTerms)) {
      return null;
    }

    const conditions = Object.keys(availableTerms);
    return (
      <div className="row">
        {conditions.map((condition) => {
          const name = `defaultTerm${condition}`;
          const availableTermForCondition = availableTerms[condition];
          const options = availableTermForCondition.map((term) => ({
            value: term,
            label: `${term} months`,
          }));
          return (
            <div className="col-sm-4">
              <label htmlFor={name}>{`Default Term - ${condition}`}</label>
              {availableTermForCondition.length > 0 && (
                <Input
                  type="select"
                  name={name}
                  id={name}
                  options={options}
                  value={defaultTermsByCondition[condition] || null}
                  onChange={(term) =>
                    onDefaultTermForConditionChange(term, condition)
                  }
                  className="select"
                  containerClassName="select-container"
                />
              )}
              {availableTermForCondition.length === 0 && (
                <div className="p-form-field-message">
                  <p>
                    <i>
                      Terms not available for <strong>{condition}</strong>{' '}
                      vehicle
                    </i>
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderMarkupDiscountField = (
    label,
    origin,
    values,
    setValue,
    type,
    max,
  ) => (
    <div className="field col-sm-3">
      <MarkupDiscount
        label={`${label} - ${origin}`}
        submitted={submitted}
        value={values[origin]}
        readOnly={isProviderAlly && label === 'Discount'}
        setValue={(v) => setValue((prev) => ({ ...prev, [origin]: v }))}
        priceAdjustmentType={type}
        max={max}
      />
    </div>
  );

  const renderInsuranceFields = () => (
    <div>
      <div className="row">
        <div className="col-sm-4">
          <FormGroup
            controlId="insuranceType"
            validationState={requiredValidationState(insuranceType, submitted)}
          >
            <ControlLabel>Insurance Type *</ControlLabel>
            <FormControl
              componentClass="select"
              value={insuranceType}
              onChange={(event) => setInsuranceType(event.target.value)}
              required={shouldDisplayInsuranceFields}
            >
              <option key={'null'} value={undefined} />
              {Object.values(InsuranceType).map((type) => (
                <option key={type} value={type}>
                  {InsuranceTypeLabelMap[type] || type}
                </option>
              ))}
            </FormControl>
            <FormControl.Feedback type="error">
              <div className="p-form-field-message">
                This is a Required field
              </div>
            </FormControl.Feedback>
          </FormGroup>
        </div>
        <div className="col-sm-4">
          <FormGroup
            controlId="insuranceProviderName"
            validationState={requiredValidationState(
              insuranceCompanyName,
              submitted,
            )}
          >
            <ControlLabel>Insurance Provider Name *</ControlLabel>
            <FormControl
              type="text"
              className="form-control"
              name="insuranceCompanyName"
              value={insuranceCompanyName}
              onChange={(e) => setInsuranceCompanyName(e.target.value)}
              required={shouldDisplayInsuranceFields}
            />
            <FormControl.Feedback type="error">
              <div className="p-form-field-message">
                This is a Required field
              </div>
            </FormControl.Feedback>
          </FormGroup>
        </div>
      </div>
      <label htmlFor="insuranceAddress">Insurance Address</label>
      <div className="row">
        <div className="col-sm-4">
          <label htmlFor="termLength">Line 1</label>
          <input
            type="text"
            className="form-control"
            name="insuranceAddressLineOne"
            id="insuranceAddressLineOne"
            value={insuranceAddressLineOne}
            onChange={(e) => setInsuranceAddressLineOne(e.target.value)}
          />
        </div>
        <div className="col-sm-4">
          <label htmlFor="termLength">Line 2</label>
          <input
            type="text"
            className="form-control"
            name="insuranceAddressLineTwo"
            id="insuranceAddressLineTwo"
            value={insuranceAddressLineTwo}
            onChange={(e) => setInsuranceAddressLineTwo(e.target.value)}
          />
        </div>
        <div className="col-sm-2">
          <label htmlFor="termLength">City</label>
          <input
            type="text"
            className="form-control"
            name="insuranceAddressCity"
            id="insuranceAddressCity"
            value={insuranceAddressCity}
            onChange={(e) => setInsuranceAddressCity(e.target.value)}
          />
        </div>
        <div className="col-sm-1" style={{ marginTop: '7px' }}>
          <FormGroup controlId="insuranceAddressStateCode">
            <ControlLabel>State Code</ControlLabel>
            <FormControl
              componentClass="select"
              value={insuranceAddressStateCode}
              onChange={(event) =>
                setInsuranceAddressStateCode(event.target.value)
              }
            >
              <option key={'null'} value={undefined} />
              {Object.keys(STATES).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </FormControl>
          </FormGroup>
        </div>
        <div className="col-sm-1">
          <label htmlFor="termLength">Zip</label>
          <input
            type="text"
            className="form-control"
            name="insuranceAddressZip"
            id="insuranceAddressZip"
            value={insuranceAddressZip}
            onChange={(e) => setInsuranceAddressZip(e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const handleInStoreSelection = (e) => {
    setSelectedInStore(e.target.value);
  };
  const handleOnlineSelection = (e) => {
    setSelectedOnline(e.target.value);
  };

  const renderRemainingForm = () => (
    <div>
      <div className="row">
        <div className="field col-sm-12">
          <label htmlFor="customerName">Description</label>
          <textarea
            className="form-control"
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="info">One line break per item</div>
        </div>
      </div>
      <div className="row">
        <div className="field col-sm-12">
          <div className="horz-radio-container">
            <label htmlFor="percent">
              Adjust Price by Percentage
              <input
                type="radio"
                name="priceAdjustmentType"
                id="percent"
                value={PriceAdjustmentType.PERCENT}
                checked={
                  priceAdjustmentTypeValue === PriceAdjustmentType.PERCENT
                }
                onChange={(e) =>
                  setPriceAdjustmentTypeValue(PriceAdjustmentType.PERCENT)
                }
              />
            </label>
            <label htmlFor="number">
              Adjust Price by Dollar Amount
              <input
                type="radio"
                name="priceAdjustmentType"
                id="number"
                value={PriceAdjustmentType.NUMBER}
                checked={
                  priceAdjustmentTypeValue === PriceAdjustmentType.NUMBER
                }
                onChange={(e) =>
                  setPriceAdjustmentTypeValue(PriceAdjustmentType.NUMBER)
                }
              />
            </label>
          </div>
        </div>
      </div>
      <div className="row">
        {renderMarkupDiscountField(
          'Markup',
          Origin.ONLINE,
          markups,
          setMarkups,
          priceAdjustmentTypeValue,
        )}
        {renderMarkupDiscountField(
          'Markup',
          Origin.INSTORE,
          markups,
          setMarkups,
          priceAdjustmentTypeValue,
        )}
        {renderMarkupDiscountField(
          'Discount',
          Origin.ONLINE,
          discounts,
          setDiscounts,
          priceAdjustmentTypeValue,
          100,
        )}
        {renderMarkupDiscountField(
          'Discount',
          Origin.INSTORE,
          discounts,
          setDiscounts,
          priceAdjustmentTypeValue,
          100,
        )}
      </div>
      {!!isProviderAlly && (
        <div className=" col-sm-12 product-info-bundle">
          * Ally products cannot have discount. Any existing discount will be
          removed..
        </div>
      )}
      <div className="row">
        {product.imageUrl && (
          <div className="field col-sm-3">
            <label>Product Image</label>
            <div className="bundle-image-container">
              <img src={product.imageUrl} alt="new upload" />
            </div>
          </div>
        )}
        <div className="field col-sm-9">
          <div className="form-group">
            <label htmlFor="productImageUploadInput">
              Upload Product Image
            </label>
            <input
              type="file"
              className="form-control-file"
              id="productImageUploadInput"
              onChange={(e) => parseImage(e)}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-4">
          <label htmlFor="productCategory">Product Category</label>
          <Input
            type="select"
            name="productCategory"
            id="productCategory"
            options={getProductCategories()}
            value={productCategorySelect}
            onChange={(category) => setProductCategorySelect(category)}
            className="select"
            containerClassName="select-container"
          />
        </div>
      </div>
      <div className="row">
        <div className="field col-sm-2 checkbox-config-container">
          <input
            className="checkbox"
            type="checkbox"
            name="backend-product"
            id="backend-product"
            value={backend}
            checked={backend}
            onChange={(e) => setBackend(e.target.checked)}
          />
          <label htmlFor="backend-product">Backend Product</label>
        </div>
      </div>
      {shouldDisplayInsuranceFields && renderInsuranceFields()}

      <div className="row">
        <div className="col-sm-4">
          <label htmlFor="termLength">Default Term - All</label>
          <Input
            type="select"
            name="termLength"
            id="termLength"
            options={DEFAULT_TERM_LENGTHS}
            value={defaultTermLength}
            onChange={(term) => setDefaultTermLength(term)}
            className="select"
            containerClassName="select-container"
          />
        </div>
        <div className="col-sm-8">
          Note: available terms vary based on the car condition and finance
          type. The terms shown within this page are all based on the default
          term of a preselected car within this Dealer’s inventory. There is a
          slight chance your selection will not be available on every vehicle
          within our applications.
        </div>
      </div>
      {renderDefaultTermsByCondition()}
      <div className="row">
        <div className="field col-sm-2 checkbox-config-container">
          <label htmlFor="enabled">Enable Product</label>
          <input
            className="checkbox"
            type="checkbox"
            name="enabled"
            id="enabled"
            value={enabled}
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
        </div>
        <div className="field col-sm-2 checkbox-config-container">
          <label htmlFor="product-is-taxed">Product is Taxed</label>
          <input
            className="checkbox"
            type="checkbox"
            name="product-is-taxed"
            id="product-is-taxed"
            value={taxed}
            checked={taxed}
            onChange={(e) => setTaxed(e.target.checked)}
          />
        </div>
        {product.source !== PenProtectionPlanSourceTypes.PROVIDER &&
          renderSelectionSettings()}
      </div>

      <div className="row">
        <div className="field col-sm-4">
          <label htmlFor="videoUrl">Video URL</label>
          <input
            type="text"
            className="form-control"
            name="videoUrl"
            id="videoUrl"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
          />
        </div>
        <div className="field col-sm-4">
          <label htmlFor="brochureUrl">Brochure URL</label>
          <input
            type="text"
            className="form-control"
            name="brochureUrl"
            id="brochureUrl"
            value={brochureUrl}
            onChange={(e) => setBrochureUrl(e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const renderHelpToolTip = (id, message) => (
    <OverlayTrigger
      placement="bottom"
      delayHide={300}
      overlay={<Tooltip id={id}>{message}</Tooltip>}
    >
      <div className="input-box-help">!</div>
    </OverlayTrigger>
  );

  const formatMileageInput = (value) =>
    value && value.replace(/\D/gi, '').length > 0
      ? Number(value.replace(/\D/gi, '')).toLocaleString()
      : '';

  const renderSelectionSettings = () => (
    <>
      <div className="field col-sm-2 checkbox-container">
        <div>In Store Selection Setting</div>
        {inStoreSelectionOptions.map(({ label, value }) => (
          <Radio
            label={label}
            value={value}
            checked={value === selectedInStore}
            onClick={handleInStoreSelection}
            key={label + selectedInStore}
          />
        ))}
      </div>
      <div className="field col-sm-2 checkbox-container">
        <div>Online Selection Setting</div>
        {onlineSelectionOptions.map(({ label, value }) => (
          <Radio
            label={label}
            value={value}
            checked={value === selectedOnline}
            onClick={handleOnlineSelection}
            key={label + selectedOnline}
          />
        ))}
      </div>
    </>
  );
  return (
    <div className="p-modal-container">
      <div className="p-modal large">
        <LoadingOverlay active={showSpinner}>
          <div className="p-modal-text">
            {product.raw.productId && <h2>Edit {product.raw.name}</h2>}
            {!product.raw.productId && <h2>Add New Product</h2>}
          </div>
          <i
            onClick={handleClose}
            className="modal-close close icon"
            alt="close"
          />
          <div className="p-modal-body">
            <Form
              id="modifyProductForm"
              name="modifyProductForm"
              noValidate
              className="p-form"
            >
              {penError && (
                <div className="row error-message-container">{penError}</div>
              )}
              {/* BEGIN PROVIDER PRODUCT TYPE */}
              {product.source === PenProtectionPlanSourceTypes.PROVIDER && (
                <div className="row">
                  {/* BEGIN PROVIDER SELECTION */}
                  <div className="field col-sm-3">
                    <FormGroup
                      controlId="provider"
                      validationState={requiredValidationState(
                        provider,
                        submitted,
                      )}
                    >
                      <ControlLabel>Provider *</ControlLabel>
                      <FormControl
                        componentClass="select"
                        name="provider"
                        onChange={(e) => {
                          selectProvider(e.target.value);
                        }}
                        disabled={product.raw.productId}
                        required
                      >
                        {!provider && (
                          <option value={-1} key={-1}>
                            Select One
                          </option>
                        )}
                        {providers.map((pro) => (
                          <option
                            key={pro.id}
                            value={pro.id}
                            selected={pro.name === provider}
                          >
                            {pro.name}
                          </option>
                        ))}
                      </FormControl>
                      <FormControl.Feedback type="error">
                        <div className="p-form-field-message">
                          This is a Required field
                        </div>
                      </FormControl.Feedback>
                    </FormGroup>
                  </div>
                  {/* BEGIN PRODUCT SELECTION */}
                  <div className="field col-sm-3">
                    <FormGroup
                      controlId="product"
                      validationState={requiredValidationStateObject(
                        selectedProduct,
                        submitted,
                      )}
                    >
                      <ControlLabel>Product *</ControlLabel>
                      <FormControl
                        componentClass="select"
                        name="product"
                        onChange={(e) => {
                          selectProduct(e.target.value);
                        }}
                        disabled={!provider || product.raw.productId}
                        required
                      >
                        {!selectedProduct && (
                          <option value={-1} key={-1}>
                            Select One
                          </option>
                        )}
                        {pen.providerProducts.map((prod) => (
                          <option
                            key={prod.raw.productId}
                            value={prod.raw.productId}
                            selected={
                              !!(
                                selectedProduct &&
                                selectedProduct.raw &&
                                prod.raw.productId ===
                                  selectedProduct.raw.productId
                              )
                            }
                          >
                            {prod.raw.name}
                          </option>
                        ))}
                      </FormControl>
                      <FormControl.Feedback type="error">
                        <div className="p-form-field-message">
                          This is a Required field
                        </div>
                      </FormControl.Feedback>
                    </FormGroup>
                  </div>
                  {/* BEGIN PROVIDER DEALER CODE */}
                  <div className="field col-sm-3">
                    <FormGroup
                      controlId="providerDealerCode"
                      validationState={requiredValidationState(
                        providerDealerCode,
                        submitted,
                      )}
                    >
                      <ControlLabel>Provider Dealer Code *</ControlLabel>
                      <FormControl
                        type="text"
                        className="form-control"
                        name="providerDealerCode"
                        value={providerDealerCode}
                        onChange={(e) => setProviderDealerCode(e.target.value)}
                        disabled={
                          (product.raw.productId &&
                            product.providerDealerCode) ||
                          !provider
                        }
                        required
                      />
                      <FormControl.Feedback type="error">
                        <div className="p-form-field-message">
                          This is a Required Field
                        </div>
                      </FormControl.Feedback>
                    </FormGroup>
                  </div>
                  {/* BEGIN PRODUCT DEALER CODE */}
                  {dealerProductCodeRequired && (
                    <div className="field col-sm-3">
                      <FormGroup
                        controlId="productDealerCode"
                        validationState={requiredValidationState(
                          productDealerCode,
                          submitted,
                        )}
                      >
                        <ControlLabel>Product Dealer Code *</ControlLabel>
                        <div className="product-dealer-code-input-container">
                          <FormControl
                            type="text"
                            className="form-control"
                            name="productDealerCode"
                            value={productDealerCode}
                            onChange={(e) =>
                              setProductDealerCode(e.target.value)
                            }
                            disabled={
                              product.raw.productId && product.productDealerCode
                            }
                            required
                          />
                        </div>
                        <FormControl.Feedback type="error">
                          <div className="p-form-field-message">
                            This is a Required Field
                          </div>
                        </FormControl.Feedback>
                      </FormGroup>
                    </div>
                  )}
                </div>
              )}
              {canShowSelectRateBookCode() ? (
                <SelectRateBookCode
                  productId={get(selectedProduct, 'raw.productId')}
                  providerDealerCode={providerDealerCode}
                  productDealerCode={productDealerCode}
                  setShowSpinner={setShowSpinner}
                  setPenError={setPenError}
                  dealerId={dealer.id}
                  rateBookCode={rateBookCode}
                  setRateBookCode={setRateBookCode}
                  lookupDisabled={rateBookLookupDisabled}
                />
              ) : null}

              {/* Add Static Product */}
              {product.source !== PenProtectionPlanSourceTypes.PROVIDER && (
                <>
                  <div className="row">
                    <div className="col-sm-12">
                      <h5 className="checkbox-group-label">
                        Associate Product with Deals containing{' '}
                        <strong>Vehicle Condition</strong> and{' '}
                        <strong>Finance Type</strong> of:
                      </h5>
                    </div>
                    <div className="field col-sm-3 horz-checkbox-container">
                      <div>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="available-new-lease"
                          id="available-new-lease"
                          value={availableNewLease}
                          checked={availableNewLease}
                          onChange={(e) =>
                            setAvailableNewLease(e.target.checked)
                          }
                        />
                        <label htmlFor="available-new-lease">
                          {Condition.NEW} - {FinanceType.LEASE}
                        </label>
                      </div>

                      <div>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="available-new-purchase"
                          id="available-new-purchase"
                          value={availableNewPurchase}
                          checked={availableNewPurchase}
                          onChange={(e) =>
                            setAvailableNewPurchase(e.target.checked)
                          }
                        />
                        <label htmlFor="available-new-purchase">
                          {Condition.NEW} - {FinanceType.PURCHASE}
                        </label>
                      </div>

                      <div>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="available-new-cash"
                          id="available-new-cash"
                          value={availableNewCash}
                          checked={availableNewCash}
                          onChange={(e) =>
                            setAvailableNewCash(e.target.checked)
                          }
                        />
                        <label htmlFor="available-new-cash">
                          {Condition.NEW} - {FinanceType.CASH}
                        </label>
                      </div>
                    </div>
                    <div className="field col-sm-3 horz-checkbox-container">
                      <div>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="available-used-purchase"
                          id="available-used-purchase"
                          value={availableUsedPurchase}
                          checked={availableUsedPurchase}
                          onChange={(e) =>
                            setAvailableUsedPurchase(e.target.checked)
                          }
                        />
                        <label htmlFor="available-used-purchase">
                          {Condition.USED} - {FinanceType.PURCHASE}
                        </label>
                      </div>

                      <div>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="available-used-cash"
                          id="available-used-cash"
                          value={availableUsedCash}
                          checked={availableUsedCash}
                          onChange={(e) =>
                            setAvailableUsedCash(e.target.checked)
                          }
                        />
                        <label htmlFor="available-used-cash">
                          {Condition.USED} - {FinanceType.CASH}
                        </label>
                      </div>
                    </div>
                    {!!dealer?.featureConfig?.useCPO?.enabled && (
                      <div className="field col-sm-3 horz-checkbox-container">
                        <div>
                          <input
                            className="checkbox"
                            type="checkbox"
                            name="available-cpo-purchase"
                            id="available-cpo-purchase"
                            value={availableCpoPurchase}
                            checked={availableCpoPurchase}
                            onChange={(e) =>
                              setAvailableCpoPurchase(e.target.checked)
                            }
                          />
                          <label htmlFor="available-cpo-purchase">
                            {Condition.CPO} - {FinanceType.PURCHASE}
                          </label>
                        </div>

                        <div>
                          <input
                            className="checkbox"
                            type="checkbox"
                            name="available-cpo-cash"
                            id="available-cpo-cash"
                            value={availableCpoCash}
                            checked={availableCpoCash}
                            onChange={(e) =>
                              setAvailableCpoCash(e.target.checked)
                            }
                          />
                          <label htmlFor="available-cpo-cash">
                            {Condition.CPO} - {FinanceType.CASH}
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-sm-12">
                      <h5 className="checkbox-group-label">
                        Should this product be included in the cap cost for the
                        following finance types?
                      </h5>
                    </div>
                    <div className="field col-sm-3 horz-checkbox-container">
                      <div>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="capcost-lease"
                          id="capcost-lease"
                          value={inCapCostForLease}
                          checked={inCapCostForLease}
                          onChange={(e) =>
                            setInCapCostForLease(e.target.checked)
                          }
                          disabled={!availableNewLease}
                        />
                        <label htmlFor="capcost-lease">
                          {FinanceType.LEASE}
                        </label>
                      </div>

                      <div>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="capcost-one-pay-lease"
                          id="capcost-one-pay-lease"
                          value={inCapCostForOnePayLease}
                          checked={inCapCostForOnePayLease}
                          onChange={(e) =>
                            setInCapCostForOnePayLease(e.target.checked)
                          }
                          disabled={!availableNewLease}
                        />
                        <label htmlFor="capcost-one-pay-lease">
                          one pay lease
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Add Pen Product */}
              {product.source === PenProtectionPlanSourceTypes.PROVIDER && (
                <>
                  <div className="row">
                    <div className="col-sm-12">
                      <h5 className="checkbox-group-label">
                        Associate Product with Deals containing Vehicle
                        Condition And Finance Type Of:
                      </h5>
                    </div>
                    <div className="field col-sm-3 horz-checkbox-container">
                      <div className="vehicle-data-input">
                        <h5 className="checkbox-group-label">New Vehicles</h5>
                      </div>
                      <div>
                        <div className="field col-sm-12">
                          <label htmlFor="newVin">
                            VIN
                            {renderHelpToolTip(
                              'new-vehicle-number-help',
                              HELP_TOOLTIPS.VIN,
                            )}
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="newVin"
                            id="newVin"
                            value={newVin}
                            onChange={(e) =>
                              setNewVin(e.target.value.slice(0, 17))
                            }
                          />
                          {isInvalidVin(newVin) && (
                            <div className="vin-warning-message">
                              Must be 17 characters
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="available-new-lease"
                          id="available-new-lease"
                          value={availableNewLease}
                          checked={availableNewLease}
                          onChange={(e) =>
                            setAvailableNewLease(e.target.checked)
                          }
                        />
                        <label htmlFor="available-new-lease">
                          {FinanceType.LEASE}
                        </label>
                      </div>

                      <div>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="available-new-purchase"
                          id="available-new-purchase"
                          value={availableNewPurchase}
                          checked={availableNewPurchase}
                          onChange={(e) =>
                            setAvailableNewPurchase(e.target.checked)
                          }
                        />
                        <label htmlFor="available-new-purchase">
                          {FinanceType.PURCHASE}
                        </label>
                      </div>

                      <div>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="available-new-cash"
                          id="available-new-cash"
                          value={availableNewCash}
                          checked={availableNewCash}
                          onChange={(e) =>
                            setAvailableNewCash(e.target.checked)
                          }
                        />
                        <label htmlFor="available-new-cash">
                          {FinanceType.CASH}
                        </label>
                      </div>
                    </div>
                    <div className="field col-sm-3 horz-checkbox-container">
                      <div className="vehicle-data-input">
                        <h5 className="checkbox-group-label">Used Vehicles</h5>
                      </div>

                      <div>
                        <div className="field col-sm-7 vehicle-number">
                          <label htmlFor="usedVin">
                            VIN
                            {renderHelpToolTip(
                              'used-vehicle-number-help',
                              HELP_TOOLTIPS.VIN,
                            )}
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="usedVin"
                            id="usedVin"
                            value={usedVin}
                            onChange={(e) =>
                              setUsedVin(e.target.value.slice(0, 17))
                            }
                          />
                          {isInvalidVin(usedVin) && (
                            <div className="vin-warning-message">
                              Must be 17 characters
                            </div>
                          )}
                        </div>
                        <div className="field col-sm-5 vehicl-mileage">
                          <label htmlFor="usedMileage">
                            Mileage
                            {renderHelpToolTip(
                              'used-mileage-help',
                              HELP_TOOLTIPS.MILEAGE,
                            )}
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="usedMileage"
                            id="usedMileage"
                            value={usedMileage.toLocaleString()}
                            onChange={(e) =>
                              setUsedMileage(formatMileageInput(e.target.value))
                            }
                          />
                        </div>
                      </div>

                      <div>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="available-used-purchase"
                          id="available-used-purchase"
                          value={availableUsedPurchase}
                          checked={availableUsedPurchase}
                          onChange={(e) =>
                            setAvailableUsedPurchase(e.target.checked)
                          }
                        />
                        <label htmlFor="available-used-purchase">
                          {FinanceType.PURCHASE}
                        </label>
                      </div>

                      <div>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="available-used-cash"
                          id="available-used-cash"
                          value={availableUsedCash}
                          checked={availableUsedCash}
                          onChange={(e) =>
                            setAvailableUsedCash(e.target.checked)
                          }
                        />
                        <label htmlFor="available-used-cash">
                          {FinanceType.CASH}
                        </label>
                      </div>
                    </div>
                    {!!dealer?.featureConfig?.useCPO?.enabled && (
                      <div className="field col-sm-3 horz-checkbox-container">
                        <div className="vehicle-data-input">
                          <h5 className="checkbox-group-label">CPO</h5>
                        </div>
                        <div>
                          <div className="field col-sm-7">
                            <label htmlFor="cpoVin">
                              VIN
                              {renderHelpToolTip(
                                'cpo-vehicle-number-help',
                                HELP_TOOLTIPS.VIN,
                              )}
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="cpoVin"
                              id="cpoVin"
                              value={cpoVin}
                              onChange={(e) =>
                                setCpoVin(e.target.value.slice(0, 17))
                              }
                            />
                            {isInvalidVin(cpoVin) && (
                              <div className="vin-warning-message">
                                Must be 17 characters
                              </div>
                            )}
                          </div>
                          <div className="field col-sm-5 vehicl-mileage">
                            <label htmlFor="cpoMileage">
                              Mileage
                              {renderHelpToolTip(
                                'cpo-mileage-help',
                                HELP_TOOLTIPS.MILEAGE,
                              )}
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="cpoMileage"
                              id="cpoMileage"
                              value={cpoMileage.toLocaleString()}
                              onChange={(e) =>
                                setCpoMileage(
                                  formatMileageInput(e.target.value),
                                )
                              }
                            />
                          </div>
                        </div>
                        <div>
                          <input
                            className="checkbox"
                            type="checkbox"
                            name="available-cpo-purchase"
                            id="available-cpo-purchase"
                            value={availableCpoPurchase}
                            checked={availableCpoPurchase}
                            onChange={(e) =>
                              setAvailableCpoPurchase(e.target.checked)
                            }
                          />
                          <label htmlFor="available-cpo-purchase">
                            {FinanceType.PURCHASE}
                          </label>
                        </div>

                        <div>
                          <input
                            className="checkbox"
                            type="checkbox"
                            name="available-cpo-cash"
                            id="available-cpo-cash"
                            value={availableCpoCash}
                            checked={availableCpoCash}
                            onChange={(e) =>
                              setAvailableCpoCash(e.target.checked)
                            }
                          />
                          <label htmlFor="available-cpo-cash">
                            {FinanceType.CASH}
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="row">
                    <div className="field col-sm-3 horz-checkbox-container">
                      <div>
                        <div className="field col-sm-12">
                          <label htmlFor="purchaseTerm">Purchase Term</label>
                          <input
                            type="number"
                            className="form-control"
                            name="purchaseTerm"
                            id="purchaseTerm"
                            value={purchaseTerm}
                            onChange={(e) =>
                              setPurchaseTerm(parseInt(e.target.value, 10))
                            }
                            disabled={
                              !availableNewPurchase &&
                              !availableUsedPurchase &&
                              !availableCpoPurchase
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-sm-12">
                      <h5 className="checkbox-group-label">
                        Should this product be included in the cap cost for the
                        following finance types?
                      </h5>
                    </div>
                    <div className="field col-sm-3 horz-checkbox-container">
                      <div>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="capcost-lease"
                          id="capcost-lease"
                          value={inCapCostForLease}
                          checked={inCapCostForLease}
                          onChange={(e) =>
                            setInCapCostForLease(e.target.checked)
                          }
                          disabled={!availableNewLease}
                        />
                        <label htmlFor="capcost-lease">
                          {FinanceType.LEASE}
                        </label>
                      </div>

                      <div>
                        <input
                          className="checkbox"
                          type="checkbox"
                          name="capcost-one-pay-lease"
                          id="capcost-one-pay-lease"
                          value={inCapCostForOnePayLease}
                          checked={inCapCostForOnePayLease}
                          onChange={(e) =>
                            setInCapCostForOnePayLease(e.target.checked)
                          }
                          disabled={!availableNewLease}
                        />
                        <label htmlFor="capcost-one-pay-lease">
                          one pay lease
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="button-input-container">
                    <button
                      className="btn btn-primary"
                      disabled={getCoverageNamesIsDisabled() || pen.fetchingCov}
                      onClick={(e) => registerProductAndGetCoverages(e)}
                    >
                      {product.raw.productId
                        ? 'Update Coverage Names'
                        : 'Get Coverage Names'}
                    </button>
                  </div>
                  {renderSelectionSettings()}
                  <div>
                    <LoadingOverlay active={showSpinner} inline="centered" />
                    <Loader active={pen.fetchingCov} inline="centered" />
                    {renderConditionsAndFinanceTypeTabs()}
                  </div>
                </>
              )}

              {(canShowRemainingForm() ||
                product.source === PenProtectionPlanSourceTypes.USER) && (
                <div>
                  <div className="row">
                    <div className="field col-sm-4">
                      <FormGroup
                        controlId="displayName"
                        validationState={requiredValidationState(
                          displayName,
                          submitted,
                        )}
                      >
                        <ControlLabel>Display Name *</ControlLabel>
                        <FormControl
                          type="text"
                          className="form-control"
                          name="displayName"
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          required
                        />
                        <FormControl.Feedback type="error">
                          <div className="p-form-field-message">
                            This is a Required Field
                          </div>
                        </FormControl.Feedback>
                      </FormGroup>
                    </div>
                    <div className="field col-sm-3 checkbox-highlight-container">
                      <input
                        className="checkbox"
                        type="checkbox"
                        name="product-highlightedText-chechbox"
                        id="product-highlightedText-chechbox"
                        value={displayHighlightedTextInput}
                        checked={displayHighlightedTextInput}
                        onChange={(e) => {
                          setDisplayHighlightedTextInput(e.target.checked);
                          if (!e.target.checked) {
                            setHighlightedText('');
                          }
                        }}
                      />
                      <label htmlFor="highlight-product">
                        Highlight Product
                      </label>
                    </div>

                    {!!displayHighlightedTextInput && (
                      <div className="field col-sm-4">
                        <FormGroup
                          controlId="highlightedText"
                          validationState={requiredValidationState(
                            highlightedText,
                            submitted,
                          )}
                        >
                          <ControlLabel>Highlight Text *</ControlLabel>
                          <FormControl
                            type="text"
                            className="form-control"
                            name="highlightedText"
                            value={highlightedText}
                            onChange={(e) => setHighlightedText(e.target.value)}
                            required
                          />
                          <FormControl.Feedback type="error">
                            <div className="p-form-field-message">
                              This is a Required Field
                            </div>
                          </FormControl.Feedback>
                        </FormGroup>
                      </div>
                    )}
                    {product.source === PenProtectionPlanSourceTypes.USER && (
                      <div className="field col-sm-4">
                        <FormGroup
                          controlId="price"
                          validationState={requiredValidationState(
                            retailPrice,
                            submitted,
                          )}
                        >
                          <ControlLabel>Price *</ControlLabel>
                          <FormControl
                            type="number"
                            className="form-control"
                            name="retailPrice"
                            value={retailPrice}
                            onChange={(e) => setRetailPrice(e.target.value)}
                            required
                          />
                          <FormControl.Feedback type="error">
                            <div className="p-form-field-message">
                              This is a Required field
                            </div>
                          </FormControl.Feedback>
                        </FormGroup>
                      </div>
                    )}
                  </div>
                  {renderRemainingForm()}
                </div>
              )}
            </Form>
          </div>
          <div className="p-modal-actions product-actions">
            <button
              className="btn btn-default btn-large"
              onClick={() => handleClose()}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary btn-large btn-large-default"
              onClick={(e) => handleSubmit(e)}
              disabled={saveIsDisabled()}
            >
              Save
            </button>
          </div>
        </LoadingOverlay>
      </div>
    </div>
  );
}

ProductModal.propTypes = {
  close: PropTypes.func,
  product: PropTypes.object,
  actions: PropTypes.object,
  dealer: PropTypes.object,
  pen: PropTypes.object,
};

const mapStateToProps = ({ pen }) => ({
  pen,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...penActions,
      updateDealer: updateDealerRequest,
    },
    dispatch,
  ),
});

const ModifyProductModal = createModal(
  connect(mapStateToProps, mapDispatchToProps)(ProductModal),
);

export default ModifyProductModal;
